import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import './SmsSettings.css';

/**
 * SMS Settings — MSG91 configuration, editable without Azure access.
 *
 * Lives in the `sms-settings` document in the settings container. Every field
 * is an override: left blank, it falls back to the env var it used to be read
 * from. The auth key is write-only — the server sends back only a hint, so
 * there is nothing here to read it out of.
 */
export function SmsSettings() {
  const queryClient = useQueryClient();

  const [apiKey, setApiKey] = useState('');
  const [otpTemplateId, setOtpTemplateId] = useState('');
  const [otpVar, setOtpVar] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['smsSettings'],
    queryFn: adminApi.getSmsSettings,
  });

  const stored = data?.settings ?? null;
  const env = data?.env;

  useEffect(() => {
    if (data) {
      // Never prefilled — the key is not sent to us, and an empty box is what
      // "leave it as it is" looks like on save.
      setApiKey('');
      setOtpTemplateId(stored?.otpTemplateId ?? '');
      setOtpVar(stored?.otpVar ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi.updateSmsSettings({
        apiKey: apiKey.trim(),
        otpTemplateId: otpTemplateId.trim(),
        otpVar: otpVar.trim(),
      }),
    onMutate: () => {
      setSaveStatus('saving');
      setSaveError('');
    },
    onSuccess: () => {
      setSaveStatus('saved');
      setApiKey('');
      queryClient.invalidateQueries({ queryKey: ['smsSettings'] });
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
  });

  const dirty =
    apiKey.trim().length > 0 ||
    otpTemplateId !== (stored?.otpTemplateId ?? '') ||
    otpVar !== (stored?.otpVar ?? '');

  const keySource = stored?.apiKeySet
    ? `Set here (${stored.apiKeyHint}).`
    : env?.apiKeySet
      ? 'Not set here — currently using the Azure environment value.'
      : 'Not configured anywhere. Sending an SMS code will fail.';

  const templateSource = stored?.otpTemplateId
    ? null
    : env?.otpTemplateIdSet
      ? 'Blank — currently using the Azure environment value.'
      : 'Blank, and no environment value either. Sending an SMS code will fail.';

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>SMS</h2>
      </div>

      {isLoading ? (
        <div className="loading">Loading SMS settings…</div>
      ) : (
        <div className="sms-form">
          <div className="sms-field">
            <label className="feature-field-label" htmlFor="sms-apiKey">MSG91 auth key</label>
            <p className="sms-source">{keySource}</p>
            <input
              id="sms-apiKey"
              type="password"
              autoComplete="new-password"
              className="feature-field-input sms-input-mono"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={stored?.apiKeySet ? 'Leave blank to keep the current key' : 'Paste the key from MSG91'}
            />
            <p className="sms-field-help">
              From the MSG91 dashboard → Authkey. Saving a value here replaces the
              Azure <code>MSG91_API_KEY</code> for every environment reading this database.
              The key is never shown again after saving — only its last four characters.
            </p>
          </div>

          <div className="sms-field">
            <label className="feature-field-label" htmlFor="sms-otpTemplateId">OTP template ID</label>
            {templateSource && <p className="sms-source">{templateSource}</p>}
            <input
              id="sms-otpTemplateId"
              type="text"
              className="feature-field-input sms-input-mono"
              value={otpTemplateId}
              onChange={(e) => setOtpTemplateId(e.target.value)}
              placeholder="64f0a1b2c3d4e5f6a7b8c9d0"
            />
            <p className="sms-field-help">
              The DLT-registered flow template that carries the login code. India requires
              every commercial SMS to match a template pre-registered with the telecom
              regulator, so this cannot be free text. Blank falls back to
              the Azure <code>MSG91_OTP_TEMPLATE_ID</code>.
            </p>
          </div>

          <div className="sms-field">
            <label className="feature-field-label" htmlFor="sms-otpVar">OTP variable name</label>
            <input
              id="sms-otpVar"
              type="text"
              className="feature-field-input sms-input-mono"
              value={otpVar}
              onChange={(e) => setOtpVar(e.target.value)}
              placeholder="otp"
            />
            <p className="sms-field-help">
              The placeholder inside that template the code is substituted into. Blank
              means <code>otp</code>, which is what the template normally uses — change
              it only if MSG91 shows a different variable name.
            </p>
          </div>

          <div className="feature-actions">
            <button
              type="button"
              className="feature-save-btn"
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveStatus === 'saving'}
            >
              {saveStatus === 'saving' ? 'Saving…' : 'Save'}
            </button>
            {saveStatus === 'saved' && <span className="status-saved">Saved.</span>}
            {saveStatus === 'error' && <span className="status-error">Error: {saveError}</span>}
          </div>

          <p className="sms-field-help">
            These are only used when Settings → Defaults → phone OTP transport is set to
            SMS, or when a WhatsApp send fails and falls back to SMS.
          </p>
        </div>
      )}
    </div>
  );
}
